/**
 * Organizer home (`/me`).
 *
 * The entry point of the organizer area: who the visitor is, whether the
 * invitation gate is open, and the four places to go next. Event data stays on
 * `/me/events`, so the only thing borrowed from it here is the pair of counters
 * the summary line needs.
 */

import { CalendarDays, History, ShieldCheck, Ticket, UserRound } from 'lucide-react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import { PageHeader } from '../../components/layout/page-header'
import { ErrorBlock, LoadingBlock } from '../../components/layout/states'
import { Badge } from '../../components/ui/badge'
import { Card, CardContent } from '../../components/ui/card'
import { useCurrentUser } from '../../hooks/use-auth'
import { useEventList } from '../../hooks/use-events'

export function MeHomePage() {
  const { t } = useTranslation()
  const { user, isInvited, isAdmin } = useCurrentUser()
  const eventList = useEventList()

  // Administrators pass every gate the invitation opens, so the badge reports
  // what the account can actually do rather than one of the two reasons.
  const canCreateEvent = isInvited || isAdmin

  const displayName = user?.nickname ?? user?.email ?? ''
  // The avatar is decorative; the accessible name is always the identity text
  // next to it, so a single initial is enough.
  const initial = displayName.slice(0, 1).toUpperCase()

  const navigationCards = [
    {
      to: '/me/profile',
      icon: UserRound,
      title: t('me.profileCard'),
      body: t('me.profileCardBody'),
    },
    {
      to: '/me/invite',
      icon: Ticket,
      title: t('me.inviteCard'),
      body: canCreateEvent ? t('me.inviteCardBodyActive') : t('me.inviteCardBody'),
    },
    {
      to: '/me/events',
      icon: CalendarDays,
      title: t('me.eventsCard'),
      body: t('me.eventsCardBody'),
    },
    {
      to: '/me/history',
      icon: History,
      title: t('me.historyCard'),
      body: t('me.historyCardBody'),
    },
  ]

  // The console is not part of a member's area, so its entry only exists for an
  // administrator: the guard on `/admin` would otherwise be a link nobody can see.
  if (isAdmin) {
    navigationCards.push({
      to: '/admin',
      icon: ShieldCheck,
      title: t('nav.admin'),
      body: t('me.adminCardBody'),
    })
  }

  return (
    <div className="container-narrow flex flex-col gap-6">
      <PageHeader
        breadcrumbs={[{ label: t('nav.home'), to: '/' }, { label: t('nav.me') }]}
        title={t('me.title')}
      />

      <Card>
        <CardContent className="flex items-center gap-4 pt-4 sm:pt-5">
          <div
            aria-hidden
            className="bg-primary text-primary-foreground flex size-12 shrink-0 items-center justify-center rounded-full text-lg font-semibold"
          >
            {initial || '?'}
          </div>
          <div className="flex min-w-0 flex-col gap-1.5">
            <p className="truncate text-base font-semibold">{displayName}</p>
            {user?.email ? (
              <p className="text-muted-foreground truncate text-sm">{user.email}</p>
            ) : null}
            <Badge variant={canCreateEvent ? 'success' : 'secondary'} className="self-start">
              {canCreateEvent ? t('me.inviteBadgeActive') : t('me.inviteBadgeInactive')}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4 sm:pt-5">
          {eventList.isPending ? <LoadingBlock label={t('common.loading')} /> : null}
          {eventList.isError ? (
            <ErrorBlock
              title={t('error.internal_error')}
              retryLabel={t('common.retry')}
              onRetry={() => void eventList.refetch()}
            />
          ) : null}
          {eventList.data ? (
            <p className="text-sm">
              {t('me.activitySummary', {
                created: eventList.data.created.length,
                joined: eventList.data.joined.length,
              })}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <nav className="grid gap-3 sm:grid-cols-2">
        {navigationCards.map((card) => (
          <Link
            key={card.to}
            to={card.to}
            className="focus-visible:ring-ring block rounded-xl focus-visible:ring-2 focus-visible:outline-none"
          >
            <Card className="hover:bg-accent/40 h-full transition-colors">
              <CardContent className="flex items-start gap-3 pt-4 sm:pt-5">
                <card.icon className="text-primary mt-0.5 size-4 shrink-0" aria-hidden />
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium">{card.title}</span>
                  <span className="text-muted-foreground text-sm">{card.body}</span>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </nav>
    </div>
  )
}
